import { Button, Card, Col, Flex, Input, Row, Select, Table, Tag, Typography } from "antd";
import React, { useState } from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { routes } from "../../../../../../constants";
import {
  baseRouteMaker,
  baseRouteMakerForSuperAdmin,
} from "../../../../../../constants/routeConsts";
import Actions from "../../../../../../redux/actions";
import Spinner from "../../../../../common/spinner/Spinner";
import { dateObjToDispViewDate, timeObjToDispViewTimeWithSuffix } from "../../../../../../utils/utils";
import styles from "./CustomerInfo.module.scss";
import { colors } from "../../../../../../constants/colors";
import { getAppointmentsAction } from "../../../../../../redux/services/appointments/appointmentsServices";
import { SearchOutlined } from "@ant-design/icons";
import useDebounce from "../../../../../../hooks/useDebounce";
import { appConstConfig } from "../../../../../../constants/config/appConstConfig";
import { ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";


const CustomerInfoAppointmentDetails = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch an action */
  const dispatch = useDispatch();
  /* dispatch an action */

  /* For navigating between pages */
  const navigate = useNavigate();
  /* For navigating between pages */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  /* loading dependencies */

  /* table dependencies */
  const [columns, setColumns] = useState([]);
  const { appointments } = useSelector((state) => state.appointments);
  const { selectedCustomerId } = useSelector((state) => state.customers);
  const [searchValue, setSearchValue] = useState("");
  const statusList = [
    {value:'COMPLETED', label:"Completed"},
    {value:'INCOMPLETE', label:"Incomplete"},
    {value:'ASSIGNED_TO_SERVICE_PROVIDER,APPROVED_BY_SERVICE_PROVIDER', label:"Received"},
    {value:'REJECTED', label:"Rejected"},
    {value:'REFUND_PROCESSED', label:"Refund Processed"},
    {value:'REFUND_REQUESTED', label:"Refund Requested"}
  ]
  const [selectedStatusObj, setSelectedStatusObj] = useState(null);
  const { debouncedValue: debouncedSearchValue, debounceLoading } = useDebounce(
    searchValue,
    500
  );
  const [pagination, setPagination] = useState({
    pageNo: appConstConfig.DEFAULT_TABLE_PAGE_NUMBER,
    pageSize: appConstConfig.DEFAULT_TABLE_PAGE_SIZE,
  });
  const paginationObj = {
    total: appointments?.total,
    current: pagination?.pageNo,
    pageSize: pagination?.pageSize,
    onChange: (pageNo, pageSize) => setPagination({ pageNo, pageSize }),
    pageSizeOptions: appConstConfig.DEFAULT_PAGE_SIZE_OPTIONS_ARR,
    showSizeChanger: true,
  };
  /* table dependencies */

  /* function definition to get table data */
  const handleGetTableData = () => {
    const dataToBeSent = {
      page: debouncedSearchValue !== "" ? 1 : pagination?.pageNo,
      limit: pagination?.pageSize,
      search: debouncedSearchValue,
      order_status:[selectedStatusObj],
      customer_id : selectedCustomerId
    };
    dispatch(getAppointmentsAction(dataToBeSent, setLoading));
  };
  /* function definition to get table data */

  useEffect(() => {
    handleGetTableData();
  }, [debouncedSearchValue, selectedStatusObj, pagination]);

  /* initial dependencies rendering */
  useEffect(() => {
    /* table column config */
    const tempTableColumns = [
      {
        title: t("chats.orderID"),
        dataIndex: "order_number",
        key: "order_number",
      },
      {
        title: t("Modules.Appointments.orderDate"),
        dataIndex: "created_at",
        key: "created_at",
        render: (_, record) => (
          <p>{record?.created_at ? `${dateObjToDispViewDate(record?.created_at, "ddmmyyyy")} - ${timeObjToDispViewTimeWithSuffix(dayjs(record?.created_at).utc().local().format('ddd, MMM D, H:mm z'))}` : "-"}</p>
        ),
      },
      {
        title: t("Modules.Appointments.orderType"),
        dataIndex: "order_type",
        key: "order_type",
      },
      {
        title: t("Modules.Provider.serviceProviderName"),
        dataIndex: "name",
        render: (_, record) => <p>{record?.organization?.name ?? "-"}</p>,
      },
      {
        title: t("appointmentInfo.customerName"),
        dataIndex: "user",
        key: "user",
        render: (_, record) => (
          <p>
            {record?.user?.first_name || record?.user?.last_name
              ? `${record?.user?.first_name} ${record?.user?.last_name}`
              : "-"}
          </p>
        ),
      },
      {
        title: t("ProfileSetup.countryLabel"),
        dataIndex: "country",
        key: "country",
        render: (country) => country ? country?.emoji+" "+country?.name : "-",
      },
      {
        title: t("appointmentInfo.paymentStatus"),
        key: "is_payment_completed",
        dataIndex: "is_payment_completed",
        render: (_, { is_payment_completed }) => (
          <>
            <Tag
              bordered={false}
              style={{
                background: is_payment_completed
                  ? colors.lightTeal
                  : colors.lightPinkishRed,
                color: is_payment_completed
                  ? colors.darkTeal
                  : colors.darkMaroon,
              }}
            >
              {is_payment_completed ? t("Modules.Appointments.paid") : t("Modules.Appointments.due")}
            </Tag>
          </>
        ),
      },
      {
        title: t("revnue.revenue"),
        dataIndex: "total_amount",
        key: "total_amount",
        render: (_, record) => (
          <p>{record?.total_amount ? record?.total_amount?.toFixed(2) : "-"}</p>
        ),
      },
      {
        title: t("appointmentInfo.appointmentStatus"),
        key: "system_status",
        dataIndex: "system_status",
        render: (_, { system_status }) => {
          const getColorBasedOnStatus = (status) => {
            switch (status) {
              case "COMPLETED":
                return { background: colors.lightTeal, color: colors.darkTeal };

              case "INCOMPLETE":
                return {
                  background: colors.lightPastelBlue,
                  color: colors.electricBlue,
                };

              case "ASSIGNED_TO_SERVICE_PROVIDER":
                return {
                  background: colors.darkGoldenRod,
                  color: colors.mango,
                };

              case "APPROVED_BY_SERVICE_PROVIDER":
                return {
                  background: colors.darkGoldenRod,
                  color: colors.mango,
                };

              case "REJECTED":
                return {
                  background: colors.lightPinkishRed,
                  color: colors.darkMaroon,
                };

              case "REFUND_PROCESSED":
                return { background: colors.slatePurple, color: colors.indigo };

              case "REFUND_REQUESTED":
                return { background: "#ebe9e6", color: "#696969" };

              default:
                break;
            }
          };
          return (
            <>
              <Tag
                bordered={false}
                style={getColorBasedOnStatus(system_status)}
              >
                {[
                  "ASSIGNED_TO_SERVICE_PROVIDER",
                  "APPROVED_BY_SERVICE_PROVIDER",
                ].includes(system_status)
                  ? "RECEIVED"
                  : system_status}
              </Tag>
            </>
          );
        },
      },
      {
        title: t("tabs.appointmentInfo"),
        key: "action",
        align: "center",
        render: (_, record) => {
          return (
            <>
              <Typography.Link onClick={(e) => handleViewDetails(e, record)}>
                View
              </Typography.Link>
            </>
          );
        },
      },
    ];
    /* table column config */
    setColumns(tempTableColumns);
  }, []);
  /* initial dependencies rendering */

  const handleViewDetails = (e, row) => {
    dispatch(Actions.createAction(Actions.SELECTED_CUSTOMER_ID_FOR_APPOINTMENT_VIEW_DETAILS, {selectedCustomerIdForAppointmentViewDetails:""}))
    navigate(baseRouteMakerForSuperAdmin(routes.CUSTOMER_INFO_DETAILS_APPOINTMENTS_VIEW));
    if (
      row?._id &&
      row?._id !== "" &&
      row?._id !== null &&
      row?._id !== undefined
    ) {
      const selectedCustomerIdForAppointmentViewDetails = row?._id;
      dispatch(Actions.createAction(Actions.SELECTED_CUSTOMER_ID_FOR_APPOINTMENT_VIEW_DETAILS, {selectedCustomerIdForAppointmentViewDetails}))
    }
  };


  return (
    <div className={styles.appointmentsContainer}>
      <Flex vertical gap="2rem">
      <Row gutter={[16,16]}>
          <Col span={1} style={{ display: "flex", alignItems: "center" }}>
          <Button
            type="default"
            shape="circle"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() =>
              navigate(baseRouteMakerForSuperAdmin(routes.CUSTOMER_INFO))
            }
          />
            </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Input
              size="large"
              suffix={<SearchOutlined />}
              placeholder={t("Modules.customer.search")}
              onChange={(event) => setSearchValue(event.target.value)}
            />
          </Col>
          <Col span={8} style={{ display: "flex", alignItems: "center" }}>
            <Select
              style={{ width: "100%" }}
              placeholder={t("Modules.Appointments.selectStatus")}
              onChange={(value, element) => setSelectedStatusObj(value)}
              allowClear
              showSearch
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {statusList &&
                statusList?.map((item) => (
                  <Select.Option key={item?.value} value={item?.value}>
                    {item?.label}
                  </Select.Option>
                ))}
            </Select>
          </Col>
        </Row>
        {loading || debounceLoading ? (
          <Spinner />
        ) : (
          <Table dataSource={appointments?.data} columns={columns} pagination = {paginationObj} />
        )}
      </Flex>
    </div>
  );
};

export default CustomerInfoAppointmentDetails;
